import { AFFILIATE_CARRIERS } from './affiliateCarriers';

export type Carrier = {
  name: string;
  slug: string;
  categories: ('term' | 'permanent' | 'no_exam' | 'final_expense')[];
  amBestRating?: 'A++' | 'A+' | 'A' | 'A-';
  strengths?: string[];
  logo: { light: string; dark?: string; alt: string };
  website?: string;
  states?: string[];
};

export const CARRIERS: Carrier[] = [
  // Term Life Specialists
  {
    name: 'Prudential',
    slug: 'prudential',
    categories: ['term'],
    amBestRating: 'A+',
    strengths: ['Established reputation', 'Flexible term options'],
    logo: { light: '/logos/carriers/prudential.png', alt: 'Prudential logo' },
    website: 'https://prudential.com'
  },
  {
    name: 'Banner Life',
    slug: 'banner-life',
    categories: ['term'],
    amBestRating: 'A+',
    strengths: ['Competitive rates', 'Simplified underwriting'],
    logo: { light: '/logos/carriers/banner-life.png', alt: 'Banner Life logo' },
    website: 'https://bannerlife.com'
  },
  {
    name: 'Protective Life',
    slug: 'protective-life',
    categories: ['term'],
    amBestRating: 'A+',
    strengths: ['Strong financial backing', 'Diverse product options'],
    logo: { light: '/logos/carriers/protective-life.png', alt: 'Protective Life logo' },
    website: 'https://protective.com'
  },
  {
    name: 'Pacific Life',
    slug: 'pacific-life',
    categories: ['term'],
    amBestRating: 'A+',
    strengths: ['West Coast focus', 'Excellent customer service'],
    logo: { light: '/logos/carriers/pacific-life.png', alt: 'Pacific Life logo' },
    website: 'https://pacificlife.com'
  },
  {
    name: 'Lincoln Financial',
    slug: 'lincoln-financial',
    categories: ['term'],
    amBestRating: 'A+',
    strengths: ['Large coverage amounts', 'Flexible payment options'],
    logo: { light: '/logos/carriers/lincoln-financial.png', alt: 'Lincoln Financial logo' },
    website: 'https://lincolnfinancial.com'
  },
  {
    name: 'John Hancock',
    slug: 'john-hancock',
    categories: ['term'],
    amBestRating: 'A+',
    strengths: ['Vitality program rewards', 'Digital-first experience'],
    logo: { light: '/logos/carriers/john-hancock.png', alt: 'John Hancock logo' },
    website: 'https://johnhancock.com'
  },
  {
    name: 'AIG',
    slug: 'aig',
    categories: ['term'],
    amBestRating: 'A',
    strengths: ['Global financial strength', 'Innovative products'],
    logo: { light: '/logos/carriers/aig.png', alt: 'AIG logo' },
    website: 'https://aig.com'
  },
  {
    name: 'Transamerica',
    slug: 'transamerica',
    categories: ['term'],
    amBestRating: 'A+',
    strengths: ['Nationwide availability', 'Multiple product lines'],
    logo: { light: '/logos/carriers/transamerica.png', alt: 'Transamerica logo' },
    website: 'https://transamerica.com'
  },

  // Whole Life & Permanent
  {
    name: 'Northwestern Mutual',
    slug: 'northwestern-mutual',
    categories: ['permanent'],
    amBestRating: 'A++',
    strengths: ['Highest financial ratings', 'Dividend track record'],
    logo: { light: '/logos/carriers/northwestern-mutual.png', alt: 'Northwestern Mutual logo' },
    website: 'https://northwesternmutual.com'
  },
  {
    name: 'MassMutual',
    slug: 'massmutual',
    categories: ['permanent'],
    amBestRating: 'A++',
    strengths: ['Mutual company structure', 'Strong dividends'],
    logo: { light: '/logos/carriers/massmutual.png', alt: 'MassMutual logo' },
    website: 'https://massmutual.com'
  },
  {
    name: 'Guardian Life',
    slug: 'guardian-life',
    categories: ['permanent'],
    amBestRating: 'A++',
    strengths: ['Premium permanent products', 'Financial planning focus'],
    logo: { light: '/logos/carriers/guardian-life.png', alt: 'Guardian Life logo' },
    website: 'https://guardianlife.com'
  },
  {
    name: 'New York Life',
    slug: 'new-york-life',
    categories: ['permanent'],
    amBestRating: 'A++',
    strengths: ['Oldest mutual company', 'Conservative management'],
    logo: { light: '/logos/carriers/new-york-life.png', alt: 'New York Life logo' },
    website: 'https://newyorklife.com'
  },

  // No-Exam / Accelerated
  {
    name: 'Ladder Life',
    slug: 'ladder-life',
    categories: ['no_exam', 'term'],
    amBestRating: 'A',
    strengths: ['Instant, fully online term life with flexible coverage', 'No medical exam for most applicants'],
    logo: { light: '/images/carriers/ladder-life.png', alt: 'Ladder Life logo' },
    website: AFFILIATE_CARRIERS.find(c => c.id === 'ladder-life')?.referralLink || ''
  },
  {
    name: 'Ethos',
    slug: 'ethos',
    categories: ['no_exam'],
    amBestRating: 'A-',
    strengths: ['Fully digital platform', 'No-exam up to $2M'],
    logo: { light: '/logos/carriers/ethos.png', alt: 'Ethos logo' },
    website: 'https://ethoslife.com'
  },
  {
    name: 'Foresters Financial',
    slug: 'foresters-financial',
    categories: ['no_exam'],
    amBestRating: 'A',
    strengths: ['Member benefits included', 'Simplified issue options'],
    logo: { light: '/logos/carriers/foresters-financial.png', alt: 'Foresters Financial logo' },
    website: 'https://foresters.com'
  },
  {
    name: 'SBLI',
    slug: 'sbli',
    categories: ['no_exam'],
    amBestRating: 'A+',
    strengths: ['Direct-to-consumer focus', 'Competitive no-exam rates'],
    logo: { light: '/logos/carriers/sbli.png', alt: 'SBLI logo' },
    website: 'https://sbli.com'
  },

  // Final Expense / Guaranteed Issue
  {
    name: 'Mutual of Omaha',
    slug: 'mutual-of-omaha',
    categories: ['final_expense'],
    amBestRating: 'A+',
    strengths: ['Guaranteed acceptance options', 'Affordable final expense'],
    logo: { light: '/logos/carriers/mutual-of-omaha.png', alt: 'Mutual of Omaha logo' },
    website: 'https://mutualofomaha.com'
  },
  {
    name: 'Aetna',
    slug: 'aetna',
    categories: ['final_expense'],
    amBestRating: 'A',
    strengths: ['Healthcare integration', 'Simplified underwriting'],
    logo: { light: '/logos/carriers/aetna.png', alt: 'Aetna logo' },
    website: 'https://aetna.com'
  },
  {
    name: 'Gerber Life',
    slug: 'gerber',
    categories: ['final_expense'],
    amBestRating: 'A+',
    strengths: ['Guaranteed issue plans', 'No medical exam required'],
    logo: { light: '/logos/carriers/gerber.png', alt: 'Gerber Life logo' },
    website: 'https://gerberlife.com'
  }
];

export const CATEGORY_LABELS = {
  term: 'Term Life Insurance Specialists',
  permanent: 'Whole Life & Permanent Coverage',
  no_exam: 'No-Exam Options',
  final_expense: 'Final Expense / Guaranteed Issue'
} as const;

export const RATING_LABELS = {
  'A++': 'Superior',
  'A+': 'Superior',
  'A': 'Excellent',
  'A-': 'Excellent'
} as const;